// ─── Routes ─────────────────────────────────────────────────────────────────
export const ROUTE_PATHS = {
  HOME: '/',
} as const;

// ─── Types ───────────────────────────────────────────────────────────────────
export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export interface FinancingResult {
  monthlyRate: number;
  totalInterest: number;
  totalCost: number;
}

export interface RentalMonth {
  month: string;
  shortMonth: string;
  nightlyRate: number;
  weeklyRate: number;
  occupancyPct: number;
  season: 'nebensaison' | 'vorsaison' | 'hauptsaison' | 'hochsaison';
  potentialMonthlyIncome: number;
}

export interface Excursion {
  name: string;
  distance: string;
  description: string;
  icon: string;
  angle: number; // degrees from north for map placement
  distancePx: number; // px from center on the map
}

// ─── Rental Data (Rügen 2026 market rates) ───────────────────────────────────
export const RENTAL_DATA: RentalMonth[] = [
  { month: 'Januar',    shortMonth: 'Jan', nightlyRate: 85,  weeklyRate: 595,  occupancyPct: 18, season: 'nebensaison',  potentialMonthlyIncome: 595  },
  { month: 'Februar',   shortMonth: 'Feb', nightlyRate: 88,  weeklyRate: 616,  occupancyPct: 20, season: 'nebensaison',  potentialMonthlyIncome: 630  },
  { month: 'März',      shortMonth: 'Mär', nightlyRate: 95,  weeklyRate: 665,  occupancyPct: 30, season: 'nebensaison',  potentialMonthlyIncome: 840  },
  { month: 'April',     shortMonth: 'Apr', nightlyRate: 120, weeklyRate: 840,  occupancyPct: 50, season: 'vorsaison',    potentialMonthlyIncome: 1260 },
  { month: 'Mai',       shortMonth: 'Mai', nightlyRate: 140, weeklyRate: 980,  occupancyPct: 65, season: 'vorsaison',    potentialMonthlyIncome: 1715 },
  { month: 'Juni',      shortMonth: 'Jun', nightlyRate: 155, weeklyRate: 1085, occupancyPct: 75, season: 'vorsaison',    potentialMonthlyIncome: 2015 },
  { month: 'Juli',      shortMonth: 'Jul', nightlyRate: 195, weeklyRate: 1365, occupancyPct: 92, season: 'hauptsaison',  potentialMonthlyIncome: 3150 },
  { month: 'August',    shortMonth: 'Aug', nightlyRate: 205, weeklyRate: 1435, occupancyPct: 95, season: 'hauptsaison',  potentialMonthlyIncome: 3360 },
  { month: 'September', shortMonth: 'Sep', nightlyRate: 145, weeklyRate: 1015, occupancyPct: 70, season: 'vorsaison',    potentialMonthlyIncome: 1890 },
  { month: 'Oktober',   shortMonth: 'Okt', nightlyRate: 115, weeklyRate: 805,  occupancyPct: 55, season: 'vorsaison',    potentialMonthlyIncome: 1260 },
  { month: 'November',  shortMonth: 'Nov', nightlyRate: 90,  weeklyRate: 630,  occupancyPct: 22, season: 'nebensaison',  potentialMonthlyIncome: 700  },
  { month: 'Dezember',  shortMonth: 'Dez', nightlyRate: 185, weeklyRate: 1295, occupancyPct: 60, season: 'hochsaison',   potentialMonthlyIncome: 2590 },
];

export const SEASON_COLORS: Record<string, string> = {
  nebensaison: '#60a5fa',
  vorsaison:   '#34d399',
  hauptsaison: '#f97316',
  hochsaison:  '#a855f7',
};

export const SEASON_LABELS: Record<string, string> = {
  nebensaison: 'Nebensaison',
  vorsaison:   'Vor-/Nachsaison',
  hauptsaison: 'Hauptsaison',
  hochsaison:  'Hochsaison',
};

// ─── Excursion Data ───────────────────────────────────────────────────────────
export const EXCURSIONS: Excursion[] = [
  { name: 'Kap Arkona',          distance: '15 km', description: 'Nördlichster Punkt Rügens – Leuchttürme & Wikingerdorf',   icon: '🏛️', angle: 355, distancePx: 90  },
  { name: 'Hiddensee',           distance: '30 km', description: 'Autofreie Insel – Naturparadies in der Ostsee',            icon: '⛵', angle: 260, distancePx: 140 },
  { name: 'Bergen auf Rügen',    distance: '40 km', description: 'Kreisstadt – Einkaufen, Kultur & Marienkirche',            icon: '🏘️', angle: 165, distancePx: 165 },
  { name: 'Nationalpark Jasmund',distance: '41 km', description: 'UNESCO Welterbe – Königsstuhl & Kreidefelsen',             icon: '🏔️', angle: 120, distancePx: 175 },
  { name: 'Sassnitz',            distance: '41 km', description: 'Fischerhafen & Fähre nach Schweden',                       icon: '⚓', angle: 130, distancePx: 170 },
  { name: 'Binz',                distance: '48 km', description: 'Bekanntester Badeort – Bäderarchitektur & Seebrücke',      icon: '🏖️', angle: 145, distancePx: 195 },
  { name: 'Sellin',              distance: '57 km', description: 'Romantische Seebrücke & Villenarchitektur',                icon: '🌊', angle: 155, distancePx: 215 },
];

// ─── Financing Calculator ─────────────────────────────────────────────────────
export function calculateFinancing(
  purchasePrice: number,
  downPayment: number,
  interestRate: number,
  termYears: number,
): FinancingResult {
  const loanAmount = purchasePrice - downPayment;
  const monthlyInterest = interestRate / 100 / 12;
  const numPayments = termYears * 12;

  if (monthlyInterest === 0) {
    const monthly = loanAmount / numPayments;
    return { monthlyRate: monthly, totalInterest: 0, totalCost: loanAmount };
  }

  const monthlyRate =
    (loanAmount * monthlyInterest * Math.pow(1 + monthlyInterest, numPayments)) /
    (Math.pow(1 + monthlyInterest, numPayments) - 1);

  const totalCost = monthlyRate * numPayments;
  const totalInterest = totalCost - loanAmount;

  return { monthlyRate, totalInterest, totalCost };
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

export function formatPct(value: number): string {
  return `${value} %`;
}

// ─── Scrolling ────────────────────────────────────────────────────────────────
export function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}
